<!-- Header -->
<div class="flex items-center justify-between p-6 border-b border-gray-200" xmlns="http://www.w3.org/1999/html">
  <h2 class="text-2xl font-bold text-gray-900">Pine Economic Analysis Tool</h2>
  <button
    type="button"
    class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
    [mat-dialog-close]="false"
  >
    <i class="fas fa-times text-xl"></i>
  </button>
</div>

<mat-stepper linear #stepper>
  <!--
  ==============================================================================================
  Location & Plantation Size Step
  ==============================================================================================
  -->
  <mat-step [stepControl]="locationAndPlantation">
    <form class="mt-2" [formGroup]="locationAndPlantation" (keydown.enter)="$event.preventDefault()">
      <ng-template matStepLabel>Location & Plantation Size</ng-template>
      <div class="m-6">
        <h5>{{locationDetails}}</h5>
        <div class="grid grid-cols-2 gap-4 mt-3">
          <div>
            <app-pine-popover showLabel="true" labelFor="areaToBePlanted"
                              infoPopoverKey="areaToBePlanted"
                              labelText="Confirm area to be planted with Pine (ha)"></app-pine-popover>
            <input
              formControlName="areaToBePlanted" id="areaToBePlanted" type="number" placeholder=""
              class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90">

            @if (this.locationAndPlantation.get('areaToBePlanted').errors ) {
              <div class="error-msg">The Value needs to be between 5 and 1000</div>
            }
          </div>
        </div>

        <div class="flex mt-2 mb-2 gap-4">
          <label>Use Default Value: </label>
          <esk-switch
            (click)="$event.stopPropagation()"
            [checked]="useDefaultLocation()"
            (checkedChange)="setUseLocationDefault($event)"></esk-switch>
          <label [class]="useDefaultLocation() ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(useDefaultLocation()) }}
          </label>
        </div>

        <ng-template #siteQualityHeading>
          <div class="flex items-center gap-2">
            <span>Plantation Site Quality Class Details</span>

            <app-pine-popover
              [showLabel]="false"
              labelFor=""
              infoPopoverKey="potentialThinningAndHarvest"
              labelText="">
            </app-pine-popover>
          </div>
        </ng-template>

        <app-accordion collapsible="true">
          <app-accordion-item [heading]="siteQualityHeading">
            <div class="m-2">
              <p class="mt-2">The plantation growth rates, and therefore achievable yields at thinning and final harvest used in
                this tool, are predicted based on the plantation site quality class assigned to this location, as
                described below.</p>
              <p class="mt-2">
                This site is located in the following Tasmanian bioregion: {{ bioregion }} <br> <br>
                and has the following enterprise suitability breakdown for <i>Pinus radiata</i>:
              </p>

              <table class="w-1/2 table-fixed border border-gray-300 rounded-lg overflow-hidden text-sm mt-1">
                <tbody>
                  @for (quali of siteQualityDetails; track quali.siteQuality; let i =$index) {
                    <tr>
                      <td class="w-3/5 px-2 py-2 text-right">
                        {{ quali.siteQuality }}
                      </td>

                      <td class="w-1/5 px-4 py-2 text-right">
                        {{ quali.area | number }}
                      </td>

                      <td class="w-1/5 px-4 py-2 text-left text-gray-500 flex justify-between items-center">
                        {{ quali.valueType }}
                        @if(i === 0) {
                          <app-pine-popover showLabel="true" labelFor=""
                                            infoPopoverKey="wellSuitedArea"
                                            labelText=""></app-pine-popover>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>

              <p class="mt-2">
                Based on the above parameters, the following site quality class was assigned: {{ siteQuality }} <br> <br>
                The following ages for thinning and harvest of this plantation are recommended for this site quality class:
              </p>

              <table class="w-1/2 table-fixed border border-gray-300 rounded-lg overflow-hidden text-sm mt-1">
                <tbody>
                  @for (tAndH of thinAndHarvest; track tAndH.siteQuality; let i = $index) {
                    <tr>
                      <td class="w-2/5 px-2 py-2 text-right">
                        {{ tAndH.siteQuality }}
                      </td>

                      <td class="w-1/5 px-4 py-2 text-right">
                        {{ tAndH.area }}
                      </td>

                      <td class="w-2/5 px-4 py-2 text-left text-gray-500 flex justify-between items-center">
                        {{ tAndH.valueType }}
                        @if(i === 0) {
                          <app-pine-popover showLabel="true" labelFor=""
                                            infoPopoverKey="thinning"
                                            labelText=""></app-pine-popover>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </app-accordion-item>
        </app-accordion>
      </div>

      <app-pine-stepper-footer position="start" [fg]="locationAndPlantation"></app-pine-stepper-footer>
    </form>
  </mat-step>
  <!--
 ==============================================================================================
 Establishment Step
 ==============================================================================================
 -->
  <mat-step [stepControl]="establishment">
    <form class="mt-2" [formGroup]="establishment" (keydown.enter)="$event.preventDefault()">
      <ng-template matStepLabel>Establishment</ng-template>
      <div class="m-6">
        <h5>Establishment Cost</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-3 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="allInclusive"
                                infoPopoverKey="allInclusiveCost"
                                labelText="All Inclusive Cost  ($/ha)"></app-pine-popover>
              <input formControlName="allInclusiveCost" id="allInclusive" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                [disabled]="showEstablishmentDetailedCost">
            </div>
            @if (showEstablishmentDetailedCost) {
              <div>
                <app-pine-popover showLabel="true" labelFor="sitePreparation" showInfo=false
                                  showDollarInfo="true" dollarPopoverKey="sitePrep"
                                  labelText="Site Preparation ($/ha)"></app-pine-popover>
                <input formControlName="sitePreparation" id="sitePreparation" type="number" placeholder=""
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90">
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="weedControl"
                                  showDollarInfo="true" dollarPopoverKey="estWeedCtrl" showInfo="false"
                                  labelText="Weed Control ($/ha)"></app-pine-popover>
                <input formControlName="weedControl" id="weedControl" type="number" placeholder=""
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="seedlings" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="seedlings"
                                  labelText="Seedlings ($/ha)"></app-pine-popover>
                <input formControlName="seedlings" id="seedlings" type="number" placeholder=""
                       class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="planting" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="planting"
                                  labelText="Planting ($/ha)"></app-pine-popover>
                <input formControlName="planting" id="planting" type="number" placeholder=""
                       class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="fertilising" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="fertilising"
                                  labelText="Fertilising ($/ha)"></app-pine-popover>
                <input formControlName="fertilising" id="fertilising" type="number" placeholder=""
                       class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="gameControl" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="estGameCtrl"
                                  labelText=" Game Control ($/ha)"></app-pine-popover>
                <input formControlName="gameControl" id="gameControl" type="number" placeholder=""
                       class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
            }
          </div>
        </div>

        <div class="flex mt-2 gap-4">
          <label>Show/Hide Detailed Cost: </label>
          <esk-switch
            [checked]="showEstablishmentDetailedCost"
            (click)="$event.stopPropagation()"
            (checkedChange)="setShowEstablishmentDetailedCost($event)"></esk-switch>
          <label [class]="showEstablishmentDetailedCost ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(showEstablishmentDetailedCost) }}
          </label>
        </div>

        <h5 class="mt-3">Planning Cost</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="forestPracticePlan"
                                infoPopoverKey="fppEstablishment"
                                showDollarInfo="true" dollarPopoverKey="estFpp"
                                labelText="Forest Practices Plan - FPP ($/plan)"></app-pine-popover>
              <input
                formControlName="fpp" id="forestPracticePlan" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
            <div>
              <app-pine-popover showLabel="true" labelFor="feesAndLevis"
                                infoPopoverKey="feesAndLeviesEstablishment"
                                showDollarInfo="true" dollarPopoverKey="estFeesLevies"
                                [defaultValue]="getDefaultVal('establishment.feesAndLevies')"
                                labelText="Fees and Levies ($/ha)"></app-pine-popover>
              <input
                formControlName="feesAndLevies" id="feesAndLevis" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
            <div>
              <app-pine-popover showLabel="true" labelFor="ptr"
                                infoPopoverKey="ptr"
                                showDollarInfo="true" dollarPopoverKey="ptr"
                                [defaultValue]="getDefaultVal('establishment.ptr')"
                                labelText="Private Timber Reserve - PTR ($/site)"></app-pine-popover>
              <input
                formControlName="ptr" id="ptr" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
          <div class="flex mt-2 gap-4">
            <app-pine-popover showLabel="true" labelFor=""
                              infoPopoverKey="useDefaultEstablishment"
                              labelText="Use Default Values: "></app-pine-popover>
            <esk-switch
              (click)="$event.stopPropagation()"
              [checked]="useEstablishmentDefault()"
              (checkedChange)="setUseEstablishmentDefault($event)"></esk-switch>
            <label [class]="useEstablishmentDefault() ? 'switch-on-txt' : 'switch-off-txt'">
              {{ getSwitchStatusText(useEstablishmentDefault()) }}
            </label>
          </div>
          <p class="mt-3">
            <b>Note:</b>
            <br>
            <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
              href="https://pft.tas.gov.au/species-guides"
              target="_blank">
              More information on establishing Radiata pine
            </a>
            <br>
            The Farm Woodlot Tool within Farm and Forest Mapper can help you estimate requirements for establishment, including fencing requirements.
          </p>
        </div>
      </div>
      <app-pine-stepper-footer [fg]="establishment"></app-pine-stepper-footer>
    </form>
  </mat-step>
  <!--
  ==============================================================================================
  Post Establishment Step
  ==============================================================================================
  -->
  <mat-step [stepControl]="postEstablishment">
    <form [formGroup]="postEstablishment" (keydown.enter)="$event.preventDefault()">
      <ng-template matStepLabel>Post Establishment</ng-template>
      <div class="m-6">
        <h5>Post Establishment Cost</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-3 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="total"
                                infoPopoverKey="postEstablishmentTotal"
                                labelText="Total  ($/ha)"></app-pine-popover>
              <input
                formControlName="total" id="total" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                [disabled]="showPostEstablishmentDetailedCost">
            </div>
            @if (showPostEstablishmentDetailedCost) {
              <div>
                <app-pine-popover showLabel="true" labelFor="postWeedControl" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="postEstWeedCtrl"
                                  labelText="Weed Control ($/ha)"></app-pine-popover>
                <input
                  formControlName="weedControl" id="postWeedControl" type="number" placeholder=""
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="refillPlanting" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="refillPlanting"
                                  labelText="Refill planting ($/ha)"></app-pine-popover>
                <input
                  formControlName="refillPlanting" id="refillPlanting" type="number" placeholder=""
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="postGameControl" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="postEstGameCtrl"
                                  labelText="Game Control ($/ha)"></app-pine-popover>
                <input
                  formControlName="gameControl" id="postGameControl" type="number" placeholder=""
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
              <div>
                <app-pine-popover showLabel="true" labelFor="complianceReporting" showInfo="false"
                                  showDollarInfo="true" dollarPopoverKey="complianceReporting"
                                  labelText="Compliance Reporting ($/ha)"></app-pine-popover>
                <input
                  formControlName="complianceReporting" id="complianceReporting" type="number" placeholder=""
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </div>
            }
          </div>
        </div>

        <div class="flex mt-2 gap-4">
          <label>Show/Hide Detailed Cost: </label>
          <esk-switch
            [checked]="showPostEstablishmentDetailedCost"
            (click)="$event.stopPropagation()"
            (checkedChange)="setShowPostEstablishmentDetailedCost($event)"
          ></esk-switch>
          <label [class]="showPostEstablishmentDetailedCost ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(showPostEstablishmentDetailedCost) }}
          </label>
        </div>

        <h5 class="mt-3">Annual Maintenance Costs</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="annualMaintenanceCost"
                                infoPopoverKey="AnnualMaintenanceCost"
                                showDollarInfo="true" dollarPopoverKey="annualMaintenanceCost"
                                labelText="Annual Maintenance Costs ($/ha)"></app-pine-popover>
              <input
                formControlName="annualMaintenance" id="annualMaintenanceCost" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
          <div class="flex mt-2 gap-4">
            <app-pine-popover showLabel="true" labelFor=""
                              infoPopoverKey="useDefaultPostEstablishment"
                              labelText="Use Default Values: "></app-pine-popover>
            <esk-switch
              (click)="$event.stopPropagation()"
              [checked]="usePostEstablishmentDefault()"
              (checkedChange)="setUsePostEstablishmentDefault($event)"></esk-switch>
            <label [class]="usePostEstablishmentDefault() ? 'switch-on-txt' : 'switch-off-txt'">
              {{ getSwitchStatusText(usePostEstablishmentDefault()) }}
            </label>
          </div>
          <p class="mt-3">
            <b>Note:</b>
            <br>
            <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
               href="https://pft.tas.gov.au/species-guides"
               target="_blank">
              More information on establishing Radiata pine
            </a>
            <br>
            <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
               href="https://pft.tas.gov.au/managing-trees"
               target="_blank">
              Managing trees post establishment
            </a>
          </p>
        </div>
      </div>
      <app-pine-stepper-footer [fg]="postEstablishment"></app-pine-stepper-footer>
    </form>
  </mat-step>
  <!--
 ==============================================================================================
 Carbon Step
 ==============================================================================================
 -->
  <mat-step [stepControl]="carbon">
    <form [formGroup]="carbon" (keydown.enter)="$event.preventDefault()">
      <ng-template matStepLabel>Carbon</ng-template>
      <div class="m-6">
        @if (isACCUError)
        {
          <button class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm shadow-sm"
                  (click)="recalculateACCU()">The DCCEEW FullCAM system was unavailable to calculate ACCU yields. Click this button to attempt the calculation again
          </button>
        }
        <div class="flex mt-2 gap-4">
          <app-pine-popover showLabel="true" labelFor=""
                            infoPopoverKey="modelCarbon"
                            labelText="Do you want to model a carbon plantation forestry method schedule 1 project? ">
          </app-pine-popover>
          <esk-switch
            [checked]="modelCarbonPlantation"
            (click)="$event.stopPropagation()"
            (checkedChange)="setModelCarbonPlantation($event)"
          ></esk-switch>
          <label [class]="modelCarbonPlantation ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(modelCarbonPlantation) }}
          </label>
        </div>
        <h5>Project Setup Costs</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="registrationCost"
                                infoPopoverKey="establishmentCostCarbon"
                                showDollarInfo="true" dollarPopoverKey="establishmentCost"
                                labelText="Carbon Registration & Consultancy Costs ($/site)"></app-pine-popover>
              <input
                formControlName="establishmentCost" id="registrationCost" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
        </div>
        <h5 class="mt-3">Compliance Cost</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="annualOffsetReport"
                                infoPopoverKey="annualOffsetReportCarbon"
                                showDollarInfo="true" dollarPopoverKey="annualOffset"
                                labelText="Annual Offset Report ($/site)"></app-pine-popover>
              <input
                formControlName="annualOffset" id="annualOffsetReport" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
            <div>
              <app-pine-popover showLabel="true" labelFor="audit"
                                infoPopoverKey="auditCarbon"
                                showDollarInfo="true" dollarPopoverKey="carbonAudit"
                                labelText="Audit ($/site)"></app-pine-popover>
              <input
                formControlName="audit" id="audit" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
        </div>
        <h5 class="mt-3">ACCU Sale Price</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="accuSalePrice"
                                infoPopoverKey="accuSalesCarbon"
                                showDollarInfo="true" dollarPopoverKey="carbonACCU"
                                labelText="ACCU Sale Price ($/ACCU)"></app-pine-popover>
              <input
                formControlName="accu" id="accuSalePrice" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90">
            </div>
          </div>
        </div>
        <div class="flex mt-2 gap-4">
          <app-pine-popover showLabel="true" labelFor=""
                            infoPopoverKey="useDefaultCarbon"
                            labelText="Use Default Values: "></app-pine-popover>
          <esk-switch
            (click)="$event.stopPropagation()"
            [checked]="useCarbonDefault()"
            (checkedChange)="setUseCarbonDefault($event)"></esk-switch>
          <label [class]="useCarbonDefault() ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(useCarbonDefault()) }}
          </label>
        </div>
        <p class="mt-3">
          <b>Note:</b>
          <br>
          <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
             href="https://cer.gov.au/document_page/understanding-your-plantation-forestry-project-simple-method-guide"
             target="_blank">
            More information available at Understanding your plantation forestry project - simple method guide
          </a>
          <br><br>
          Carbon forestry projects can provide an additional source of income through the generation and sale of Australian Carbon Credit Units (ACCUs), however they are complex projects with long-term obligations, reporting and auditing requirements.
          <br>
          Most landowners engage a specialist carbon project manager or consultant to assist with project design, eligibility assessment, FullCAM modelling, Clean Energy Regulator registration, reporting and ongoing compliance. A directory of carbon project managers operating in Tasmania is available through the PFT Service Directory:
          <br>
          <b>PFT Carbon Project Manager Directory</b>
          <br>
          <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
             href="https://pft.tas.gov.au/directory/carbon-project-manager" target="_blank">Carbon Project Manager</a>
          <br><br>
          The ACCU values for this location are calculated from the Department of Climate Change, Energy, the Environment and Water (DCCEEW)
          Full Carbon Accounting Model (FullCAM) - refer
          <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
             href="https://www.dcceew.gov.au/climate-change/publications/full-carbon-accounting-model-fullcam"
             target="_blank">
            Full Carbon Accounting Model (FullCAM) - DCCEEW
          </a>. The Pine Economic Analysis Tool uses the
          FullCAM 2024 public release beta version. The Latitude and Longitude coordinates used to retrieve ACCU from FullCAM for this location are: {{coordinates}}
        </p>
      </div>
      <app-pine-stepper-footer [fg]="carbon"></app-pine-stepper-footer>
    </form>
  </mat-step>
  <!--
 ==============================================================================================
 Thinning Step
 ==============================================================================================
 -->
  <mat-step [stepControl]="thinning">
    <form [formGroup]="thinning" (keydown.enter)="$event.preventDefault()">
      <ng-template matStepLabel>Thinning</ng-template>
      <div class="m-6">
        <div class="flex mt-2 gap-4">
          <label>Do you want to model a commercial thinning? </label>
          <esk-switch
            [checked]="modelCommercialThinning()"
            (click)="$event.stopPropagation()"
            (checkedChange)="setModelCommercialThinning($event)"
          ></esk-switch>
          <label [class]="modelCommercialThinning() ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(modelCommercialThinning()) }}
          </label>
        </div>
        <table class="table-auto w-4/5">
          <thead>
          <tr class="border-b border-gray-300  h-8">
            <th class="pl-2 text-left">Product:</th>
            <th class="text-left">1. Small Sawlogs</th>
            <th class="text-left">2. Posts</th>
            <th class="text-left">3. Pulplogs</th>
            <th class="">
              <app-pine-popover showLabel="false" labelFor=""
                                infoPopoverKey="thinningHeaderInfo"
                                labelText=""></app-pine-popover>
            </th>
          </tr>
          </thead>
          <tbody>
          @for(row of thinningData; track row) {
            <tr class="border-b border-gray-300  h-8" [formGroup]="row.formGroup">
              <td class="pl-2">
                {{ row.product }}
              </td>
              <td class="">
                <input
                  formControlName="smallSawlogs" [type]="row.type" placeholder="" [step]="row.step"
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </td>
              <td class="">
                <input
                  formControlName="posts" [type]="row.type" placeholder="" [step]="row.step"
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </td>
              <td class="">
                <input
                  formControlName="pulplogs" [type]="row.type" placeholder="" [step]="row.step"
                  class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                >
              </td>
              @if (row.info) {
                <td>
                    <app-pine-popover showLabel="false" labelFor=""
                                      [infoPopoverKey]="row.info" [showInfo]="row.showInfo"
                                      [showDollarInfo]="row.showDollar" [dollarPopoverKey]="row.dollar"
                                      labelText=""></app-pine-popover>
                </td>
              }
            </tr>
          }

          </tbody>
        </table>
        <h5 class="mt-3">Planning Costs</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="thinningForestPracticePlan"
                                infoPopoverKey="thinningFpp"
                                showDollarInfo="true" dollarPopoverKey="thinningFPP"
                                labelText="Forest Practices Plan - FPP ($/plan)"></app-pine-popover>
              <input
                formControlName="fpp" id="thinningForestPracticePlan" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
            <div>
              <app-pine-popover showLabel="true" labelFor="thinningFeesAndLevis"
                                infoPopoverKey="thinningFeesLevies"
                                showDollarInfo="true" dollarPopoverKey="thinningFeesLevies"
                                [defaultValue]="getDefaultVal('thinning.feesAndLevies')"
                                labelText="Fees and Levies ($/ha)"></app-pine-popover>
              <input
                formControlName="feesAndLevies" id="thinningFeesAndLevis" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
        </div>
        <h5 class="mt-3">Roading</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover labelFor="roadMaintenance" showLabel="true"
                                infoPopoverKey="thinningRoadMaintenance"
                                showDollarInfo="true" dollarPopoverKey="thinningRoadMaintenance"
                                labelText="Road Maintenance ($/t)"></app-pine-popover>
              <input
                formControlName="roadMaintenance" id="roadMaintenance" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
        </div>
        <div class="flex mt-2 gap-4">
          <app-pine-popover showLabel="true"
                            infoPopoverKey="useDefaultThinning"
                            labelText="Use Default Values: "></app-pine-popover>
          <esk-switch
            (click)="$event.stopPropagation()"
            [checked]="useThinningDefault()"
            (checkedChange)="setUseThinningDefault($event)"
          ></esk-switch>
          <label [class]="useThinningDefault() ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(useThinningDefault()) }}
          </label>
        </div>
        <p class="mt-3">
          <b>Note:</b>
          <br>
          <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
             href="https://pft.tas.gov.au/harvesting-and-selling"
             target="_blank">
            More information on harvesting and selling
          </a>
        </p>
      </div>
      <app-pine-stepper-footer [fg]="thinning"></app-pine-stepper-footer>
    </form>
  </mat-step>
  <!--
 ==============================================================================================
 Harvest Step
 ==============================================================================================
 -->
  <mat-step [stepControl]="harvest">
    <form class="flex flex-col h-full" [formGroup]="harvest" (keydown.enter)="$event.preventDefault()">
      <ng-template matStepLabel>Harvest</ng-template>
      <div class="m-6">
        <table class="table-auto w-4/5">
          <thead>
          <tr class="border-b border-gray-300  h-8">
            <th class="pl-2 text-left">Product:</th>
            <th class="text-left">1. Large Sawlogs</th>
            <th class="text-left">2. Small Sawlogs</th>
            <th class="text-left">3. Pulplogs</th>
            <th>
              <app-pine-popover showLabel="false" labelFor=""
                                infoPopoverKey="harvestHeaderInfo"
                                labelText=""></app-pine-popover>
            </th>
          </tr>
          </thead>
          <tbody>
            @for(row of harvestData; track row) {
              <tr class="border-b border-gray-300  h-8" [formGroup]="row.formGroup">
                <td class="pl-2">{{ row.product }}</td>
                <td class="">
                  <input
                    formControlName="largeSawlogs" [type]="row.type" placeholder="" [step]="row.step"
                    class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                  >
                </td>
                <td class="">
                  <input
                    formControlName="smallSawlogs" [type]="row.type" placeholder="" [step]="row.step"
                    class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                  >
                </td>
                <td class="">
                  <input
                    formControlName="pulplogs" [type]="row.type" placeholder="" [step]="row.step"
                    class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
                  >
                </td>
                @if (row.info) {
                  <td>
                    <app-pine-popover showLabel="false" labelFor=""
                                      [infoPopoverKey]="row.info" [showInfo]="row.showInfo"
                                      [showDollarInfo]="row.showDollar" [dollarPopoverKey]="row.dollar"
                                      labelText=""></app-pine-popover>
                  </td>
                }
              </tr>
            }

          </tbody>
        </table>
        <h5 class="mt-3">Planning Costs</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="harvestForestPracticePlan"
                                labelText="Forest Practices Plan - FPP ($/plan)"
                                showDollarInfo="true" dollarPopoverKey="harvestFPP"
                                infoPopoverKey="harvestFpp"></app-pine-popover>
              <input
                formControlName="fpp" id="harvestForestPracticePlan" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
            <div>
              <app-pine-popover showLabel="true" labelFor="harvestFeesAndLevis"
                                labelText="Fees and Levies ($/ha)"
                                showDollarInfo="true" dollarPopoverKey="harvestFeesLevies"
                                [defaultValue]="getDefaultVal('harvest.feesAndLevies')"
                                infoPopoverKey="harvestFeesLevies"></app-pine-popover>
              <input
                formControlName="feesAndLevies" id="harvestFeesAndLevis" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
        </div>
        <h5 class="mt-3">Roading</h5>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="roadMaintenance"
                                labelText="Road Maintenance ($/t)"
                                showDollarInfo="true" dollarPopoverKey="harvestRoadMaintenance"
                                infoPopoverKey="roadMaintenance"></app-pine-popover>
              <input
                formControlName="roadMaintenance" id="harvestRoadMaintenance" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
        </div>
        <div class="flex mt-2 gap-4">
          <app-pine-popover showLabel="true" labelFor=""
                            labelText="Use Default Values: "
                            infoPopoverKey="harvestUseDefaultValues"></app-pine-popover>
          <esk-switch
            (click)="$event.stopPropagation()"
            [checked]="useHarvestDefault()"
            (checkedChange)="setUseHarvestDefault($event)"
          ></esk-switch>
          <label [class]="useHarvestDefault() ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(useHarvestDefault()) }}
          </label>
        </div>
        <p class="mt-3">
          <b>Note:</b>
          <br>
          <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
             href="https://pft.tas.gov.au/harvesting-and-selling"
             target="_blank">
            More information on harvesting and selling
          </a>
        </p>
      </div>
      <app-pine-stepper-footer [fg]="harvest" g></app-pine-stepper-footer>
    </form>
  </mat-step>
  <!--
 ==============================================================================================
 Financial Step
 ==============================================================================================
 -->
  <mat-step [stepControl]="financial">
    <form class="flex flex-col h-full" [formGroup]="financial" (keydown.enter)="$event.preventDefault()">
      <ng-template matStepLabel>Financial</ng-template>
      <div>
        <div class="row mt-3">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-pine-popover showLabel="true" labelFor="discountRate"
                                infoPopoverKey="discountRate"
                                labelText="Discount Rate %"></app-pine-popover>
              <input
                formControlName="discountRate" id="discountRate" type="number" placeholder=""
                class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white/90"
              >
            </div>
          </div>
        </div>
        <div class="flex mt-2 gap-4">
          <label>Use Default Values: </label>
          <esk-switch
            (click)="$event.stopPropagation()"
            [checked]="useFinancialDefault()"
            (checkedChange)="setUseFinancialDefault($event)"></esk-switch>
          <label [class]="useFinancialDefault() ? 'switch-on-txt' : 'switch-off-txt'">
            {{ getSwitchStatusText(useFinancialDefault()) }}
          </label>
        </div>
        <p class="mt-3">
          <b>Note:</b>
          <br>
          <a class="text-blue-600 underline hover:text-blue-800 cursor-pointer"
             href="https://www.forestry.org.au/wp-content/uploads/2026/03/Chapter-A4-Discount-rates-Dec-2025.pdf"
             target="_blank">
            More information on discount rates
          </a>
        </p>
      </div>
      <div>
        <app-pine-stepper-footer [fg]="financial" position="end" (parentEventTransmitter)="runReport()"></app-pine-stepper-footer>
      </div>
    </form>
  </mat-step>

  <!--
 ==============================================================================================
 Summary Step
 ==============================================================================================
 -->
  @if (showSummary) {
    <mat-step>
      <form class="flex flex-col h-full" (keydown.enter)="$event.preventDefault()">
        <button class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm shadow-sm"
                (click)="exportToCsv()">Export to CSV
        </button>
        <ng-template matStepLabel>Summary Report</ng-template>
        <div class="w-full max-w-6xl mx-auto space-y-8">
          <p class="mt-2">{{peatSummary.summary}}</p>
          <div class="row">
            <ul class="list-none">
              <li class="text-lg mb-2">
                Location: {{peatSummary.location}}
              </li>
              <li class="text-lg mb-2">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>Thinning Age: {{peatSummary.thinAge}}</div>
                  <div>Harvest Age: {{peatSummary.harvestAge}}</div>
                </div>
              </li>
              <li class="text-lg mb-2">
                Carbon Modelled: {{peatSummary.carbonModelled}}
              </li>
            </ul>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h5 class="text-lg font-semibold mb-3">Undiscounted Costs</h5>
                  <p class="m-2">{{peatSummary.undiscountedCosts}}</p>
                  <table class="w-full border border-gray-300 rounded-lg overflow-hidden text-sm mt-1">
                    <thead class="bg-gray-100 text-gray-700">
                      <tr>
                        <th class="px-4 py-2 text-left">Costs</th>
                        <th class="px-4 py-2 text-right">Total</th>
                        <th class="px-4 py-2 text-right">per ha</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (key of peatSummary.costKeys ; track key) {
                        <tr>
                          @if(key !== '') {
                            <td class="px-4 py-2">{{ key }}</td>
                            <td class="px-4 py-2 text-right">
                              {{ peatSummary.getTotalTxt(key) }}
                            </td>
                            <td class="px-4 py-2 text-right">
                              {{ peatSummary.getPerHaTxt(key) }}
                            </td>
                          }
                          @else {
                            <td class="px-4 py-2"></td>
                            <td class="px-4 py-2 text-right"></td>
                            <td class="px-4 py-2 text-right"></td>
                          }
                        </tr>
                      }
                    <tr class="border-t">
                      <td class="px-4 py-2">TOTAL COST</td>
                      <td class="px-4 py-2 text-right">
                        {{ peatSummary.totalCost.totalTxt }}
                      </td>
                      <td class="px-4 py-2 text-right">
                        {{ peatSummary.totalCost.perHaTxt }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <h5 class="text-lg font-semibold mb-3">Undiscounted Revenue</h5>
                  <p class="mt-2">{{peatSummary.undiscountedRevenue}}</p>
                  <table class="w-full border border-gray-300 rounded-lg overflow-hidden text-sm mt-1">
                    <thead class="bg-gray-100 text-gray-700">
                    <th class="px-4 py-2 text-left">Costs</th>
                    <th class="px-4 py-2 text-right">units/ha or Tonnes</th>
                    <th class="px-4 py-2 text-right">Total</th>
                    <th class="px-4 py-2 text-right">per ha</th>
                    </thead>
                    <tbody>
                      @for (key of peatSummary.revenueKeys ; track key) {
                        <tr [class.bg-gray-300]="peatSummary.isTotal(key)">
                          @if(key !== '') {
                            <td class="px-4 py-2">{{ peatSummary.getRevenueName(key) }}</td>
                            <td class="px-4 py-2 text-right">{{ peatSummary.getTonnesOrUPHaTxt(key) }}</td>
                            <td class="px-4 py-2 text-right">{{ peatSummary.getTotalTxt(key, false) }}</td>
                            <td class="px-4 py-2 text-right">{{ peatSummary.getPerHaTxt(key, false) }}</td>
                          } @else {
                            <td class="px-4 py-2"></td>
                            <td class="px-4 py-2 text-right"></td>
                            <td class="px-4 py-2 text-right"></td>
                            <td class="px-4 py-2 text-right"></td>
                          }
                        </tr>
                      }
                    <tr class="border-t">
                      <td class="px-4 py-2">TOTAL REVENUE</td>
                      <td class="px-4 py-2 text-right"></td>
                      <td class="px-4 py-2 text-right">{{peatSummary.totalRevenue.totalTxt }}</td>
                      <td class="px-4 py-2 text-right">{{peatSummary.totalRevenue.perHaTxt }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
            </div>
            <div>
              <h5 class="text-lg font-semibold mb-3">Financial Analysis</h5>
              <p class="mt-2">{{peatSummary.financialAnalysis}}</p>
              <table class="w-auto border border-gray-300 rounded-lg overflow-hidden text-sm mt-1">
                <tbody>
                <tr>
                  <td class="px-4 py-2">Discount Rate</td>
                  <td class="px-4 py-2 text-right whitespace-nowrap">{{peatSummary.discountRateTxt}}</td>
                </tr>
                <tr>
                  <td class="px-4 py-2">Internal Rate of Return</td>
                  <td class="px-4 py-2 text-right whitespace-nowrap">{{peatSummary.irrTxt}}</td>
                </tr>
                <tr>
                  <td class="px-4 py-2">Net Present Value</td>
                  <td class="px-4 py-2 text-right whitespace-nowrap">{{peatSummary.npvTxt}}</td>
                </tr>
                </tbody>
              </table>
            </div>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div class="w-full">
                <h5 class="text-lg font-semibold mb-3">Cashflow Chart</h5>
                <p class="mb-4">{{peatSummary.cashflowChart}}</p>

                <div class="relative w-full h-[400px]">
                  <canvas #cashflowCanvas></canvas>
                </div>
              </div>

              <div class="w-full">
                <h5 class="text-lg font-semibold mb-3">Revenue Chart</h5>
                <p class="mb-4">{{peatSummary.revenueChart}}</p>

                <div class="relative w-full h-[400px]">
                  <canvas #carbonHarvestCanvas></canvas>
                </div>
              </div>
            </div>
            <app-accordion collapsible="true">
              <app-accordion-item heading="SUMMARY OF FINANCIAL MODEL INPUTS">
                @for(cost of peatSummary.financialInputs; track cost) {
                  <h5 class="font-bold my-2">{{cost.heading}}</h5>
                  <table class="w-3/4 border border-gray-300 rounded-lg overflow-hidden text-sm mt-1">
                    <thead class="bg-gray-100 text-gray-700">
                      @for (heading of cost.tableHeadings; track heading) {
                        <th [class]="heading.cls">{{ heading.heading }}</th>
                      }
                    </thead>
                    <tbody>
                      @for (row of cost.summary  ; track row) {
                        <tr>
                          @for (column of row ; track column) {
                            <td [class]="column.cls">{{ column.heading }}</td>
                          }
                        </tr>
                      }
                    </tbody>
                  </table>
                }
              </app-accordion-item>
            </app-accordion>
            <p class="mt-2" [innerHTML]="peatSummary.disclaimer"></p>
          </div>
        </div>
        <div>
          <app-pine-stepper-footer position="summary"
                                   (parentEventTransmitter)="backFromSummary()"
                                   (exportParentEventTransmitter)="export()">
          </app-pine-stepper-footer>
        </div>
      </form>
    </mat-step>
  }

</mat-stepper>
